import { startGupApplication } from '@/services/gup-start';
import { GupMessage } from '@/components/gup-message';
import { PageLoading } from '@/components/page-loading';
import { getGupStartBlock, type GupVisit } from '@/lib/gup';
import { getGupTaskErrorText, isGupCompanyProfileError } from '@/services/gup-errors';
import { useAuthStore } from '@/store/auth';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

export default function GupStart({ visit }: { visit: GupVisit }) {
  const navigate = useNavigate();
  const { i18n, t } = useTranslation();
  const started = useRef(false);
  const { mutate, error } = useMutation({
    mutationFn: async () => {
      const block = getGupStartBlock(visit, useAuthStore.getState().userProfile);
      if (block) throw new Error(t(`gup.blocks.${block}`));
      const { processInstanceId, taskId } = await startGupApplication(visit);
      await navigate({
        to: '/processes/$processInstanceId/task/$taskId',
        params: { processInstanceId, taskId },
        search: { responseToken: undefined },
        replace: true,
      });
    },
    retry: false,
  });
  useEffect(() => {
    if (!started.current) {
      started.current = true;
      mutate();
    }
  }, [mutate]);
  if (error)
    return (
      <GupMessage
        entry={{ visit, failure: 'failed' }}
        message={
          // The backend decides which services an individual may start; the profile can only be changed on Gov.om.
          isGupCompanyProfileError(error)
            ? t('gup.blocks.individual')
            : getGupTaskErrorText(error, i18n.language, t('gup.errors.failed'))
        }
      />
    );
  return <PageLoading />;
}
